import { attachScrollEdgeFades, updateScrollEdgeFadeState } from './attach-scroll-edge-fades';

const SCROLL_EDGE_HOST_SELECTOR = '.app-scroll-surface, .app-scroll-edge';
const SCROLL_EDGE_FADE_SIDES = ['top', 'bottom', 'left', 'right'] as const;

function ensureScrollEdgeFadeElements(viewport: HTMLElement): void {
  for (const side of SCROLL_EDGE_FADE_SIDES) {
    if (viewport.querySelector(`.app-scroll-edge__fade--${side}`)) {
      continue;
    }
    const fade = document.createElement('div');
    fade.className = `app-scroll-edge__fade app-scroll-edge__fade--${side}`;
    fade.setAttribute('aria-hidden', 'true');
    viewport.append(fade);
  }
}

function tableHead(scrollElement: HTMLElement): HTMLElement | null {
  const thead = scrollElement.querySelector('table.app-table thead');
  return thead instanceof HTMLElement ? thead : null;
}

function syncStickyTableFadeOffset(viewport: HTMLElement, scrollElement: HTMLElement): void {
  const thead = tableHead(scrollElement);
  if (!thead) {
    viewport.classList.remove('app-scroll-edge__viewport--sticky-table-header');
    viewport.style.removeProperty('--app-scroll-edge-fade-top-offset');
    return;
  }

  viewport.classList.add('app-scroll-edge__viewport--sticky-table-header');

  const apply = (): void => {
    viewport.style.setProperty('--app-scroll-edge-fade-top-offset', `${thead.offsetHeight}px`);
  };
  apply();

  if (viewport.dataset['scrollEdgeTableOffset'] === 'true') {
    return;
  }
  viewport.dataset['scrollEdgeTableOffset'] = 'true';

  const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(apply) : null;
  resizeObserver?.observe(thead);
}

function observeScrollHostContent(viewport: HTMLElement, scrollElement: HTMLElement): void {
  const sync = (): void => syncStickyTableFadeOffset(viewport, scrollElement);

  if (scrollElement.dataset['scrollEdgeContentObserver'] === 'true') {
    sync();
    return;
  }
  scrollElement.dataset['scrollEdgeContentObserver'] = 'true';

  const contentObserver = new MutationObserver(sync);
  contentObserver.observe(scrollElement, { childList: true, subtree: true });
  sync();
}

/** Viewports left behind when Angular removes the enhanced host still inside them. */
function removeOrphanScrollEdgeViewports(parent: HTMLElement): void {
  for (const child of Array.from(parent.children)) {
    if (!(child instanceof HTMLElement)) {
      continue;
    }
    if (!child.classList.contains('app-scroll-edge__viewport')) {
      continue;
    }
    if (child.querySelector('[data-scroll-edge="enhanced"]')) {
      continue;
    }
    child.remove();
  }
}

function shouldSkipEnhance(element: HTMLElement): boolean {
  if (element.dataset['scrollEdge'] === 'off' || element.dataset['scrollEdge'] === 'enhanced') {
    return true;
  }
  if (element.classList.contains('app-scroll-edge--off')) {
    return true;
  }
  if (element.classList.contains('app-dialog-shell__body')) {
    return true;
  }
  if (element.parentElement?.classList.contains('app-scroll-edge__viewport')) {
    return true;
  }
  return false;
}

/** Wrap a scroll host with viewport + fades; no-op when already enhanced or ineligible. */
export function enhanceScrollEdgeHost(element: HTMLElement): void {
  if (shouldSkipEnhance(element)) {
    return;
  }

  const parent = element.parentElement;
  if (!parent) {
    return;
  }

  removeOrphanScrollEdgeViewports(parent);

  const viewport = document.createElement('div');
  viewport.className = 'app-scroll-edge__viewport';
  parent.insertBefore(viewport, element);
  viewport.appendChild(element);

  ensureScrollEdgeFadeElements(viewport);

  element.dataset['scrollEdge'] = 'enhanced';
  attachScrollEdgeFades(viewport, element);
  observeScrollHostContent(viewport, element);
}

function syncEnhancedScrollHost(scrollElement: HTMLElement): void {
  const viewport = scrollElement.parentElement;
  if (!viewport?.classList.contains('app-scroll-edge__viewport')) {
    return;
  }
  ensureScrollEdgeFadeElements(viewport);
  syncStickyTableFadeOffset(viewport, scrollElement);
  updateScrollEdgeFadeState(viewport, scrollElement);
}

export function scanScrollEdgeHosts(root: ParentNode): void {
  root.querySelectorAll<HTMLElement>(SCROLL_EDGE_HOST_SELECTOR).forEach((element) => {
    enhanceScrollEdgeHost(element);
  });

  root.querySelectorAll<HTMLElement>('[data-scroll-edge="enhanced"]').forEach((element) => {
    syncEnhancedScrollHost(element);
  });
}
